import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  School,
  People,
  Assignment,
} from '@mui/icons-material';

import { useAuth } from '../../contexts/AuthContext';
import { useTeacher } from '../../contexts/TeacherContext';
import getConfig from '../../config/app-config';
import CourseManagementTeacher from './CourseManagementTeacher';
import EnrolledManagementTeacher from './EnrolledManagementTeacher';
import Calendar from './Calendar';
import CalendarList from './CalendarList';
import NextCourses from './NextCourses';
import dayjs from 'dayjs';

const Teacher = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const { user } = useAuth();
  const { 
    courses, 
    enrollments, 
    loading, 
    error, 
    refreshData 
  } = useTeacher();

  const config = getConfig();

  // Filter data for current teacher
  const teacherCourses = useMemo(() => {
    if (!user?._id) return [];
    return courses.filter(course => course.teacherId === user._id);
  }, [courses, user?._id]);

  const teacherEnrollments = useMemo(() => {
    if (!user?._id) return [];
    return enrollments.filter(enrollment => {
      const course = courses.find(c => c._id === enrollment.courseId);
      return course && course.teacherId === user._id;
    });
  }, [enrollments, courses, user?._id]);



  // console.log('teacherEnrollments', teacherEnrollments);
  // Calculate stats for teacher
  const stats = useMemo(() => {
    const totalStudents = teacherEnrollments.length;
    const activeEnrollments = teacherEnrollments.filter(e => 
      e.paymentStatusInfo?.isEnrollmentPaid
    ).length;
    
    return {
      totalCourses: teacherCourses.length,
      totalStudents,
      activeEnrollments
    };
  }, [teacherCourses, teacherEnrollments]);


  useEffect(() => {
    refreshData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Area Docente
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards - Hidden on mobile */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <School sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{stats.totalCourses}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Corsi Gestiti
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <People sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{stats.totalStudents}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Studenti Totali
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Assignment sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4">{stats.activeEnrollments}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Iscrizioni Attive
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

{/* Calendar Expanded */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Calendar 
              courses={teacherCourses}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <CalendarList 
              courses={teacherCourses}
              selectedDate={selectedDate}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <NextCourses 
              courses={teacherCourses}
            />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="teacher tabs">
            <Tab label="I Miei Corsi" />
            {config.SHOW_ENROLLMENTS_TEACHER_PAGE && (
              <Tab label="Iscrizioni Studenti" />
            )}
          </Tabs>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <CourseManagementTeacher
                enrollments={teacherEnrollments}
                courses={teacherCourses}
              />
            )}

            {config.SHOW_ENROLLMENTS_TEACHER_PAGE && activeTab === 1 && (
              <EnrolledManagementTeacher
                enrollments={teacherEnrollments}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Teacher; 